import { ErrorBanner } from "pages/analytics/components/AnalyticsStates";
import { formatNumber, formatPercent } from "pages/analytics/components/analyticsFormatters";
import { isCustomRangeIncomplete } from "utils/analyticsQueryParams";

const formatNullableNumber = (value) => {
  if (value === null || value === undefined) return "—";
  return formatNumber(value);
};

const formatNullablePercent = (value) => {
  if (value === null || value === undefined) return "—";
  return formatPercent(value);
};

function SummarySkeleton() {
  return (
    <div className="mb-6 overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="border-b border-gray-100 px-4 py-3">
        <div className="h-5 w-56 animate-pulse rounded bg-gray-200" />
      </div>
      <div className="space-y-3 p-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-10 animate-pulse rounded bg-gray-100" />
        ))}
      </div>
    </div>
  );
}

export default function LeadConversionSummary({
  filters,
  metrics,
  metricsLoading,
  metricsError,
}) {
  if (isCustomRangeIncomplete(filters) && !metricsLoading) {
    return (
      <p className="mb-6 text-sm italic text-gray-500">
        Select start and end dates to view lead & conversion summary.
      </p>
    );
  }

  if (metricsLoading) {
    return <SummarySkeleton />;
  }

  if (metricsError) {
    return (
      <div className="mb-6">
        <ErrorBanner message={metricsError} />
      </div>
    );
  }

  const rows = metrics?.leadConversionSummary ?? [];

  return (
    <div className="mb-6 overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="border-b border-gray-100 px-4 py-3">
        <h2 className="text-base font-semibold text-gray-900">Lead & Conversion Summary</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-100 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Funnel</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Leads</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Opt-in Rate</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Conversion</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-gray-500">
                  No funnel summary data available.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.funnelId}>
                  <td className="px-4 py-3 font-medium text-gray-900">{row.funnelName}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-900">
                    {formatNullableNumber(row.leads)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-900">
                    {formatNullablePercent(row.optInRatePercent)}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-900">
                    <div className="tabular-nums">
                      {formatNullablePercent(row.conversionPercent)}
                    </div>
                    {row.conversionLabel ? (
                      <div className="mt-0.5 text-xs font-normal text-gray-400">
                        {row.conversionLabel}
                      </div>
                    ) : (
                      <div className="mt-0.5 text-xs font-normal text-gray-400">—</div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
